import { Component, Inject, Injectable, OnDestroy } from '@angular/core';
import {
  AbstractControlOptions,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { TeamsProvider } from '../../../teams/providers/teams-provider';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeModule } from '@angular/material/tree';
import { BehaviorSubject, forkJoin, Subscription } from 'rxjs';
import { SessionExportService } from '../../../sessions/providers/session-export.service';
import moment from 'moment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AthletesProvider } from '../../../athletes/providers/athletes-provider';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { Organization } from "../../../common/models/organization";
import { Team } from "../../../common/models/team";
import { User } from "../../../common/models/user";
import { LessThanValidator } from "../../../common/validators/less-than.validator";
import { GreaterThanValidator } from "../../../common/validators/greater-than.validator";
import { SnackbarData } from "../../../common/snackbar/snackbar-data";
import { ApiError } from "../../../common/error/api-error";
import { MatButtonModule } from "@angular/material/button";
import { MatError, MatFormFieldModule } from "@angular/material/form-field";
import { NgIf } from "@angular/common";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { FlexLayoutModule } from "@angular/flex-layout";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatInputModule } from "@angular/material/input";
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * Node for to-do item
 */
export class TeamAthleteItemNode {
  public children: TeamAthleteItemNode[] = [];
  public item: string = '';
  public object: any;
}

/** Flat to-do item node with expandable and level information */
export class TeamAthleteItemFlatNode {
  public item: string = '';
  public level: number = 0;
  public expandable: boolean = false;
  public object:any;
}

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable({providedIn: 'root'})
export class AthleteToJsonTree {
  public dataChange = new BehaviorSubject<TeamAthleteItemNode[]>([]);

  get data(): TeamAthleteItemNode[] { return this.dataChange.value; }

  constructor(private teamProvider: TeamsProvider, private athleteProvider: AthletesProvider, private utils: UtilsProvider, private translater: TranslateService, @Inject(MAT_DIALOG_DATA) public dialogData:any) {
    this.initialize();
  }

  public initialize() {
    forkJoin([
      this.teamProvider.getTeams({organizationId: this.dialogData.organization.id}),
      this.athleteProvider.getAthletes(this.dialogData.organization.id)
    ]).subscribe({
      next: ([teams, athleteUsers]) => {
        const treeData: any[] = [];

        // ajoute la liste complète des athlètes (sans team)
        const leafsAthletes: any[] = [];
        let hasChildAthletes = false;
        athleteUsers.map((athleteUser) => {
          leafsAthletes[athleteUser.id!] = {value: athleteUser.firstName + ' ' + athleteUser.lastName, object: athleteUser};
          hasChildAthletes = true;
        });
        if (hasChildAthletes) {
          treeData[this.translater.instant('All_athletes_list')] = leafsAthletes.sort((a, b) => {
            a = this.utils.normalizeString(a.value);
            b = this.utils.normalizeString(b.value);

            return (a > b) ? 1 : -1;
          });
        }

        // ajoute la liste des teams et leurs athlètes
        teams.map((team) => {
          const leafs: any[] = [];
          let hasChild = false;
          team?.athletes?.map((athlete) => {
            leafs[athlete.user!.id!] = {value: athlete.user!.firstName + ' ' + athlete.user!.lastName, object: athlete.user};
            hasChild = true;
            if (hasChild) {
              const index:any = team.name;
              treeData[index] = leafs.sort((a, b) => {
                a = this.utils.normalizeString(a.value);
                b = this.utils.normalizeString(b.value);

                return (a > b) ? 1 : -1;
              });
            }
          });
        });
        // Build the tree nodes from Json object. The result is a list of `TeamAthleteItemNode` with nested
        // file node as children.
        const data = this.buildFileTree(treeData, 0);

        // Notify the change.
        this.dataChange.next(data);

      },
    })
  }

  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `TeamAthleteItemNode`.
   */
  public buildFileTree(obj: {[key: string]: any}, level: number): TeamAthleteItemNode[] {
    return Object.keys(obj).reduce<TeamAthleteItemNode[]>((accumulator, key) => {
      const value = obj[key].value || obj[key];
      const node = new TeamAthleteItemNode();
      node.item = key;
      node.object = obj[key].object;

      if (value != null) {
        if (typeof value === 'object') {
          node.children = this.buildFileTree(value, level + 1);
        } else {
          node.item = value;
        }
      }

      return accumulator.concat(node);
    }, []);
  }
}

@Component({
  selector: 'app-calendar-export',
  templateUrl: './calendar-export-dialog.component.html',
  styleUrls: ['./calendar-export-dialog.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    MatDialogModule,
    MatButtonModule,
    MatError,
    NgIf,
    MatCheckboxModule,
    MatTreeModule,
    MatIconModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  providers: [AthleteToJsonTree, SessionExportService]
})
export class CalendarExportDialogComponent implements OnDestroy {

  get f() {
    return this.exportForm.controls;
  }

  public exportForm: FormGroup;
  public isSubmitting = false;
  public organization: Organization = {} as Organization;
  public athleteUser: User = {} as User;
  public startDate: Date = new Date();
  public teams: Team[] = [];
  public users: User[] = [];
  public rows: any;
  public test: any;

  public minDate: any;

  public treeControl: FlatTreeControl<TeamAthleteItemFlatNode>;

  public dataSource: MatTreeFlatDataSource<TeamAthleteItemNode, TeamAthleteItemFlatNode>;
  public checklistValidated = true;

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  private flatNodeMap = new Map<TeamAthleteItemFlatNode, TeamAthleteItemNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  private nestedNodeMap = new Map<TeamAthleteItemNode, TeamAthleteItemFlatNode>();

  /** A selected parent node to be inserted */
  private selectedParent: TeamAthleteItemFlatNode | null = null;

  /** The new item's name */
  private newItemName = '';

  private treeFlattener: MatTreeFlattener<TeamAthleteItemNode, TeamAthleteItemFlatNode>;

  /** The selection for checklist */
  protected checklistSelection = new SelectionModel<TeamAthleteItemFlatNode>(true /* multiple */);
  private subscriptions: Subscription[] = [];

  constructor(
    public dialog: MatDialogRef<CalendarExportDialogComponent>,
    private utils: UtilsProvider,
    private fb: FormBuilder,
    private translater: TranslateService,
    private jsonTree: AthleteToJsonTree,
    private toaster: MatSnackBar,
    private sessionExportService: SessionExportService,
    @Inject(MAT_DIALOG_DATA) public data: any) {
    this.organization = data.organization;
    this.athleteUser = data.athlete;

    const selectedDate = moment(data.selectedWeek ? data.selectedWeek : new Date()).startOf('week');
    const nextWeek = selectedDate.clone();
    nextWeek.add(1, 'week');
    this.exportForm = this.fb.group({
      exportFrom: new FormControl(selectedDate, [Validators.required]),
      exportTo: new FormControl(selectedDate.clone().endOf('week'), [Validators.required]),
      deleteTimeSlots: new FormControl(false),
      importStartDate: new FormControl(nextWeek.startOf('week'), [Validators.required]),
      users: this.fb.array([], [Validators.required])
    }, {
      validator: [
        LessThanValidator('exportFrom', 'exportTo', true),
        GreaterThanValidator('exportTo', 'exportFrom', true),
      ]
    } as AbstractControlOptions);

    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<TeamAthleteItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.subscriptions.push(
      jsonTree.dataChange.subscribe((databaseData) => {
        this.dataSource.data = databaseData;
        this.checklistSelection.clear();
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /** Whether all the descendants of the node are selected. */
  public descendantsAllSelected(node: TeamAthleteItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return descendants.every((child) =>
      this.checklistSelection.isSelected(child)
    );
  }

  /** Whether part of the descendants are selected */
  public descendantsPartiallySelected(node: TeamAthleteItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  public todoItemSelectionToggle(node: TeamAthleteItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);

    // Sélectionne ou désélectionne tous les descendants
    if (this.checklistSelection.isSelected(node)) {
      this.checklistSelection.select(...descendants);
    } else {
      this.checklistSelection.deselect(...descendants);
    }

// Met à jour la sélection des parents
    this.checkAllParentsSelection(node);
  }

  /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
  public todoLeafItemSelectionToggle(node: TeamAthleteItemFlatNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
  }

  /** Checks all the parents when a leaf node is selected/unselected */
  public checkAllParentsSelection(node: TeamAthleteItemFlatNode): void {
    let parent: TeamAthleteItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
    this.validCheckListSelection();
  }

  /** Check root node checked state and change it accordingly */
  public checkRootNodeSelection(node: TeamAthleteItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every((child) =>
      this.checklistSelection.isSelected(child)
    );
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
    this.validCheckListSelection();
  }

  /** Get the parent node of a node */
  public getParentNode(node: TeamAthleteItemFlatNode): TeamAthleteItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }

    return null;
  }


  public validCheckListSelection() {
    this.checklistValidated = !(this.checklistSelection.selected.length === 0);
  }

  public submitForm() {

    this.validCheckListSelection();

    if (this.checklistValidated) {

      const users = this.exportForm.get('users') as FormArray;

      // remove all selected athletes
      while (users.length) {
        users.removeAt(users.length - 1);
      }

      // add selected athletes to form
      this.checklistSelection.selected.forEach((selected) => {
        if (selected.object) {
          users.push(new FormControl(selected.object, [Validators.required]));
        }
      });

      this.isSubmitting = true;

      const data = new SnackbarData('Agenda_Exported');
      if (this.exportForm.valid) {
        // FG: the "to" value of the exported date in the API endpoint is not inclusive
        // so we add one day here and hope that it will never get fixed in the API endpoint...
        // TODO : REMOVE TIMEZONE OFFSET
        const payload = {
          deleteTimeSlots: this.f['deleteTimeSlots'].value,
          exportFrom: this.f['exportFrom'].value.format('YYYY-MM-DD'),
          exportTo: this.f['exportTo'].value.clone().format('YYYY-MM-DD'),
          importStartDate: this.f['importStartDate'].value.format('YYYY-MM-DD'),
          users: this.f['users'].value
        };
        this.subscriptions.push(
          this.sessionExportService.export(this.organization, this.athleteUser, payload).subscribe({
            next: () => {
              this.isSubmitting = false;
              this.dialog.close(data);
            },
            error: (error: ApiError) => {
              this.isSubmitting = false;
              switch (error.status) {
                case 422:
                  this.toaster.open(this.translater.instant('Session_duplication_conflict'), '',{duration: 5000});
                  break;
                default:
                  this.dialog.close({success: false, message: error.status ?? 500});
                  break;
              }
            }
          }),
        );
      } else {
        this.utils.validateAllFormFields(this.exportForm);
        this.isSubmitting = false;
        this.utils.scrollToFormInputError();
      }
    } else {
      this.utils.scrollToFormInputError();
    }
  }

  public hasChild = (_: number, node: TeamAthleteItemFlatNode) => node.expandable;

  /** Remove toggle icon for nodes without children */
  public hasNoContent = (_: number, node: TeamAthleteItemFlatNode) => node.item === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  private transformer = (node: TeamAthleteItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.item === node.item
      ? existingNode
      : new TeamAthleteItemFlatNode();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.expandable =  node.children.length > 0;
    flatNode.object = node.object;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  private getLevel = (node: TeamAthleteItemFlatNode) => node.level;

  private isExpandable = (node: TeamAthleteItemFlatNode) => node.expandable;

  private getChildren = (node: TeamAthleteItemNode): TeamAthleteItemNode[] => node.children;

  private getObject = (node: TeamAthleteItemNode): TeamAthleteItemNode[] => node.object;
}
